<div class="max-content-width spaced-sections">
  <!-- Professional Audit/Review Interface -->
  @if (isAuditMode() && currentPaper(); as paper) {
    <div class="card overflow-hidden border-none shadow-xl mb-8 bg-white">
      <!-- High-Impact Header Section -->
      <div class="bg-white px-8 py-10 relative border-b border-slate-100">
        <div class="relative">
          <div class="flex items-center gap-3 mb-4">
            <span
              class="px-3 py-1 bg-blue-600 text-[11px] font-extrabold text-white uppercase tracking-widest rounded-lg shadow-sm"
              >Audit Mode</span
            >
          </div>
          <h1
            class="text-2xl md:text-4xl font-black text-slate-900 tracking-tight leading-tight max-w-4xl"
          >
            {{ paper.title }}
          </h1>
        </div>
      </div>

      <!-- Action & Metadata Bar -->
      <div
        class="bg-white px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-6 border-b border-slate-100"
      >
        <div class="flex flex-wrap items-center gap-x-10 gap-y-4">
          <div class="flex flex-col gap-1">
            <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest"
              >Digital Object Identifier</span
            >
            <span class="text-sm font-bold text-slate-700 font-mono">{{
              paper.doi || 'No DOI Available'
            }}</span>
          </div>
          <div class="h-8 w-px bg-slate-100 hidden md:block"></div>
          <div class="flex flex-col gap-1">
            <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest"
              >Verification Status</span
            >
            <div class="flex items-center gap-1.5">
              <span class="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
              <span class="text-sm font-bold text-emerald-600">Locked & Finalized</span>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-3 w-full md:w-auto">
          <a
            [routerLink]="['/projects', projectId(), 'completed']"
            class="flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-all text-sm font-bold group"
          >
            <i class="bi bi-arrow-left transition-transform group-hover:-translate-x-1"></i>
            Back
          </a>
          <button
            (click)="openWorkspace()"
            class="flex-1 md:flex-none flex items-center justify-center gap-2 px-8 py-3 bg-secondary hover:bg-hover text-white rounded-xl shadow-lg shadow-secondary/20 transition-all text-sm font-bold"
          >
            <i class="bi bi-fullscreen"></i>
            View Extraction
          </button>
        </div>
      </div>

      <!-- Quick Context Info -->
      <div class="bg-slate-50/50 px-8 py-4">
        <p class="text-[11px] text-slate-500 italic">
          <i class="bi bi-info-circle-fill mr-1.5"></i>
          This study has been formally included in the systematic review. You are currently in
          <strong>Audit Mode</strong>, which provides read-only access to all extracted variables
          and PDF annotations.
        </p>
      </div>
    </div>
  }

  <!-- Error Banner (top): load / no-active-paper errors only. Save/complete/skip errors for an
       active paper are shown directly below the Save button instead. Suppressed once the project is
       COMPLETED so a stale phase error never shows in place of the completed banner. -->
  @if (lastError() && !currentPaper() && !isCompleted()) {
    <div
      class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center gap-2"
    >
      <span class="flex-1">{{ lastError() }}</span>
      <button
        class="btn-small-secondary border-red-200 text-red-600 hover:bg-red-50"
        (click)="lastError.set(null)"
      >
        Dismiss
      </button>
    </div>
  }

  <!-- PI Conflict Resolution Bar -->
  @if (isPI() && !isAuditMode()) {
    @let unresolved = conflictStatus()?.unresolvedConflicts ?? 0;
    @let readyToComplete = unresolved === 0 && (conflictStatus()?.canComplete ?? false);
    <div
      class="px-3 py-2 rounded-lg border text-xs flex flex-wrap items-center gap-2"
      [class.bg-amber-50]="unresolved > 0"
      [class.border-amber-200]="unresolved > 0"
      [class.text-amber-800]="unresolved > 0"
      [class.bg-green-50]="readyToComplete"
      [class.border-green-200]="readyToComplete"
      [class.text-green-800]="readyToComplete"
      [class.bg-white]="unresolved === 0 && !readyToComplete"
      [class.border-slate-200]="unresolved === 0 && !readyToComplete"
      [class.text-slate-700]="unresolved === 0 && !readyToComplete"
    >
      @if (unresolved > 0) {
        <span class="flex-1">
          Data extraction can be completed once all conflicts have been resolved. There
          {{ unresolved === 1 ? 'is' : 'are' }}
          {{ unresolved }} unresolved conflict{{ unresolved === 1 ? '' : 's' }}.
        </span>
      } @else if (readyToComplete) {
        <span class="flex-1"> All conflicts resolved. The project is ready to complete. </span>
      }
      <button
        type="button"
        class="px-4 py-1.5 rounded-lg border font-semibold transition-colors shadow-sm flex items-center gap-1.5 shrink-0"
        [class.text-amber-700]="unresolved > 0"
        [class.bg-amber-50]="unresolved > 0"
        [class.border-amber-400]="unresolved > 0"
        [class.hover:bg-amber-100]="unresolved > 0"
        [class.text-green-700]="readyToComplete"
        [class.bg-green-50]="readyToComplete"
        [class.border-green-400]="readyToComplete"
        [class.hover:bg-green-100]="readyToComplete"
        [class.text-slate-700]="unresolved === 0 && !readyToComplete"
        [class.bg-white]="unresolved === 0 && !readyToComplete"
        [class.border-slate-300]="unresolved === 0 && !readyToComplete"
        [class.hover:bg-slate-50]="unresolved === 0 && !readyToComplete"
        (click)="navigateToConflicts()"
        data-testid="resolve-conflicts-btn"
      >
        Conflict Resolution
        @if (unresolved > 0) {
          <span
            class="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-bold bg-red-100 text-red-700 rounded-full"
            data-testid="conflict-count-badge"
          >
            {{ unresolved }}
          </span>
        } @else if (readyToComplete) {
          <span
            class="ml-1 inline-flex items-center h-5 px-2 text-xs font-bold bg-green-100 text-green-700 rounded-full"
            data-testid="ready-to-complete-badge"
          >
            Ready to complete
          </span>
        }
      </button>
    </div>
  }

  <!-- Main Workspace: two-column grid -->
  <div class="grid grid-cols-1 lg:grid-cols-4 gap-4">
    <!-- Left: Paper / Content Area -->
    @if (!isCompleted() || editingCompleted()) {
      <div class="lg:col-span-3 space-y-4">
        <!-- Loading state -->
        @if (loading() && !isCompleted()) {
          <div
            class="card card-padding flex items-center gap-3 text-slate-500 text-sm"
            data-testid="loading-indicator"
          >
            <div
              class="w-5 h-5 border-2 border-secondary border-t-transparent rounded-full animate-spin shrink-0"
            ></div>
            Loading next paper...
          </div>
        }

        <!-- All done or start extraction -->
        @if (!loading() && !currentPaper() && !isCompleted()) {
          @if (!hasRequestedFirst()) {
            <div class="card card-padding" data-testid="start-extraction">
              <div class="empty-state">
                <div class="empty-state-icon">
                  <svg
                    class="w-12 h-12 mx-auto text-secondary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                    />
                  </svg>
                </div>
                <h3 class="empty-state-title">Start Extraction</h3>
                <p class="empty-state-message">
                  Pull the next paper from the queue. It will be locked to you until you complete,
                  skip or unlock it.
                </p>
                <button
                  class="mt-6 px-6 py-3 rounded-lg font-bold text-white bg-secondary hover:bg-hover transition-colors shadow-lg shadow-secondary/20 inline-flex items-center gap-2 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  [disabled]="loading()"
                  (click)="loadNextPaper()"
                >
                  <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Start Extraction
                </button>
              </div>
            </div>
          } @else {
            <div class="card card-padding" data-testid="all-done">
              <div class="empty-state">
                <div class="empty-state-icon">&#10003;</div>
                <h3 class="empty-state-title">All done!</h3>
                <p class="empty-state-message">
                  There are no more papers available for extraction.
                </p>
              </div>
            </div>
          }
        }

        <!-- Active paper workflow -->
        @if (currentPaper(); as paper) {
          <!-- Editing-completed banner -->
          @if (editingCompleted() && !isCompleted()) {
            <div
              class="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 flex items-center justify-between gap-3"
              data-testid="editing-completed-banner"
            >
              <span> Editing a completed paper: your changes are saved in place. </span>
              <button
                class="btn-secondary shrink-0"
                (click)="onBackToQueue()"
                data-testid="back-to-queue-btn"
              >
                Back to extraction queue
              </button>
            </div>
          }

          <!-- Action Toolbar: paper metadata + actions -->
          @if (!isCompleted()) {
            <div class="bg-white rounded-xl border border-slate-200 shadow-sm p-3 text-sm">
              <div class="flex items-start gap-3">
                <div class="flex-1 min-w-0">
                  <h3 class="font-semibold text-slate-800 text-sm leading-snug">
                    {{ paper.title }}
                  </h3>
                  <div class="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    @if (paper.publicationDate) {
                      <span class="text-xs text-slate-500">{{
                        paper.publicationDate | date: 'yyyy'
                      }}</span>
                    }
                    @if (paper.authors) {
                      <span class="text-xs text-slate-500">{{ paper.authors }}</span>
                    }
                    @if (paper.doi) {
                      <span class="text-xs text-slate-500">DOI: {{ paper.doi }}</span>
                    }
                  </div>
                </div>
                @if (!editingCompleted()) {
                  <div class="shrink-0 flex items-center gap-2">
                    <button
                      class="px-4 py-1.5 rounded-lg border border-slate-300 font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      [disabled]="skipping()"
                      (click)="onSkip()"
                      data-testid="skip-btn"
                    >
                      <svg
                        class="w-4 h-4 text-slate-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2.5"
                          d="M13 5l7 7-7 7M5 5l7 7-7 7"
                        />
                      </svg>
                      {{ skipping() ? 'Skipping...' : 'Skip' }}
                    </button>
                    <button
                      class="px-4 py-1.5 rounded-lg border font-semibold text-orange-600 bg-white border-orange-400 hover:bg-orange-50 transition-colors shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      [disabled]="loading()"
                      (click)="unlockCurrent()"
                      title="Release paper lock and return to overview"
                      data-testid="unlock-btn"
                    >
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                        />
                      </svg>
                      Unlock
                    </button>
                  </div>
                }
              </div>
            </div>

            <!-- Extraction Questions Status Checklist -->
            @if (questions().length > 0) {
              <div class="card card-padding">
                <div class="flex items-center justify-between mb-3">
                  <h3 class="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Extraction Questions
                  </h3>
                  <span class="text-xs font-semibold text-slate-500">
                    {{ questionStatuses().filter((s) => s.complete).length }}
                    / {{ questions().length }} complete
                  </span>
                </div>
                <div class="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden mb-3">
                  <div
                    class="bg-blue-500 h-full rounded-full transition-all duration-300"
                    [style.width.%]="
                      (questionStatuses().filter((s) => s.complete).length / questions().length) *
                      100
                    "
                  ></div>
                </div>
                <ul class="space-y-1 max-h-64 overflow-y-auto">
                  @for (status of questionStatuses(); track status.question.id) {
                    <li
                      class="flex items-center gap-2 py-1.5 px-2 rounded-lg text-xs"
                      [class.bg-green-50]="status.complete"
                      [class.bg-amber-50]="status.needsEvidence"
                      [class.bg-red-50]="!status.answered && status.question.required"
                    >
                      <!-- Status icon -->
                      @if (status.complete) {
                        <svg
                          class="w-4 h-4 text-green-600 shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2.5"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      } @else if (status.needsEvidence) {
                        <svg
                          class="w-4 h-4 text-amber-500 shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                          />
                        </svg>
                      } @else {
                        <svg
                          class="w-4 h-4 shrink-0"
                          [class.text-red-500]="status.question.required"
                          [class.text-slate-400]="!status.question.required"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <circle cx="12" cy="12" r="10" stroke-width="2" />
                        </svg>
                      }
                      <!-- Question info -->
                      <span class="flex-1 min-w-0 truncate">
                        <span
                          class="font-medium"
                          [class.text-slate-700]="status.complete || !status.question.required"
                          [class.text-red-700]="!status.answered && status.question.required"
                        >
                          {{ status.question.text }}
                        </span>
                      </span>
                      <!-- Badges -->
                      <span
                        class="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase"
                        [class.bg-slate-100]="status.question.type !== 'BOOLEAN'"
                        [class.text-slate-500]="status.question.type !== 'BOOLEAN'"
                        [class.bg-blue-100]="status.question.type === 'BOOLEAN'"
                        [class.text-blue-700]="status.question.type === 'BOOLEAN'"
                      >
                        {{
                          status.question.type === 'FULL_TEXT'
                            ? 'Text'
                            : status.question.type === 'SINGLE_CHOICE'
                              ? 'Choice'
                              : status.question.type === 'MULTI_CHOICE'
                                ? 'Multi'
                                : status.question.type === 'NUMERIC'
                                  ? 'Num'
                                  : 'Yes/No'
                        }}
                      </span>
                      @if (status.question.required) {
                        <span
                          class="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                          [class.bg-red-100]="!status.answered"
                          [class.text-red-600]="!status.answered"
                          [class.bg-slate-100]="status.complete"
                          [class.text-slate-500]="status.complete"
                        >
                          required
                        </span>
                      }
                    </li>
                  }
                </ul>
              </div>
            }

            <!-- Open Workspace + Complete -->
            <div class="card card-padding space-y-4">
              <div class="flex items-center justify-between gap-3">
                <p class="text-sm text-slate-600">
                  Open the workspace to answer the extraction questions and mark evidence on the
                  PDF.
                </p>
                <button
                  class="btn-primary shrink-0"
                  type="button"
                  (click)="openWorkspace()"
                  data-testid="open-workspace-btn"
                >
                  Open Extraction Workspace
                </button>
              </div>

              @if (!editingCompleted()) {
                <div class="border-t border-slate-200 pt-4">
                  <button
                    class="w-full"
                    [class.btn-primary]="!isCompleteDisabled"
                    [class.btn-disabled-primary]="isCompleteDisabled"
                    [disabled]="isCompleteDisabled"
                    (click)="onComplete()"
                    data-testid="complete-btn"
                  >
                    {{ completing() ? 'Completing...' : 'Complete' }}
                  </button>
                  @if (!canComplete()) {
                    <p class="text-center text-xs text-slate-500 mt-1" data-testid="blocked-reason">
                      {{ blockedReason() }}
                    </p>
                  }
                  @if (canComplete() && hasUnifiedUnsavedChanges()) {
                    <p
                      class="text-center inline-flex w-full justify-center items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full mt-1"
                      data-testid="save-first-hint"
                    >
                      Save your progress first before completing.
                    </p>
                  }
                </div>
              }
            </div>
          }

          <!-- Page-level error (outside the modal) -->
          @if (lastError() && !workspaceOpen()) {
            <div
              class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center gap-2"
              data-testid="page-error"
            >
              <span class="flex-1">{{ lastError() }}</span>
              <button
                class="btn-small-secondary border-red-200 text-red-600 hover:bg-red-50"
                (click)="lastError.set(null)"
              >
                Dismiss
              </button>
            </div>
          }
        }
      </div>
    } @else {
      <div class="lg:col-span-3">
        <div class="card card-padding" data-testid="de-completed-banner">
          <div class="empty-state">
            <div class="empty-state-icon">&#10003;</div>
            <h3 class="empty-state-title">Data extraction completed</h3>
            <p class="empty-state-message">
              Completed on {{ completedAt() | date }}.
              {{ stats()?.totalPapers ?? completedPapers().length }} papers extracted.
            </p>
          </div>
        </div>
      </div>
    }

    <!-- Right: Sidebar (hidden when reopening a completed paper) -->
    @if (!editingCompleted()) {
      <div class="lg:col-span-1 flex flex-col gap-4">
        <!-- Extraction Progress Card -->
        @if (stats(); as s) {
          <div class="bg-white rounded-xl p-4 shadow-sm border border-slate-200/60">
            <h3 class="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
              Extraction Progress
            </h3>
            <div class="grid grid-cols-2 gap-2 text-center">
              <div class="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                <div class="text-lg font-bold text-slate-700">{{ s.unextractedPapers }}</div>
                <div class="text-[10px] text-slate-400 font-medium">To Extract</div>
              </div>
              <div class="bg-blue-50/40 p-2.5 rounded-lg border border-blue-100/50">
                <div class="text-lg font-bold text-blue-600">{{ s.partiallyExtractedPapers }}</div>
                <div class="text-[10px] text-blue-500 font-medium">In Progress</div>
              </div>
              <div class="bg-emerald-50/40 p-2.5 rounded-lg border border-emerald-100/50">
                <div class="text-lg font-bold text-emerald-600">{{ s.completedByYou }}</div>
                <div class="text-[10px] text-emerald-500 font-medium">Completed (you)</div>
              </div>
              <div class="bg-violet-50/40 p-2.5 rounded-lg border border-violet-100/50">
                <div class="text-lg font-bold text-violet-600">{{ s.completedByAll }}</div>
                <div class="text-[10px] text-violet-500 font-medium">Completed (all)</div>
              </div>
            </div>
            <div class="mt-4">
              <div class="flex justify-between text-xs font-semibold text-slate-500 mb-1">
                <span>Progress</span>
                <span>{{ s.completedByAll }} / {{ s.totalPapers }}</span>
              </div>
              <div class="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div
                  class="bg-blue-500 h-full rounded-full transition-all duration-500"
                  [style.width.%]="progressPercentage"
                ></div>
              </div>
            </div>

            <!-- Completed papers expandable list -->
            <div class="mt-3 pt-3 border-t border-slate-100">
              <button
                class="text-xs font-semibold text-secondary hover:underline"
                (click)="toggleCompletedList()"
              >
                {{ showCompletedList() ? 'Hide' : 'Show' }} completed ({{
                  completedPapers().length
                }})
              </button>
              @if (showCompletedList()) {
                <input
                  type="text"
                  class="input-field mt-2"
                  placeholder="Search title, author, year, DOI"
                  [ngModel]="completedQuery()"
                  (ngModelChange)="completedQuery.set($event)"
                  data-testid="completed-search"
                />
                <div
                  class="mt-2 rounded-lg border border-slate-200 overflow-hidden max-h-96 overflow-y-auto"
                  data-testid="completed-scroll"
                >
                  @if (filteredCompleted().length === 0) {
                    <p class="px-3 py-2 text-xs text-slate-500" data-testid="completed-empty">
                      No papers match your search.
                    </p>
                  } @else {
                    <ul>
                      @for (cp of filteredCompleted(); track cp.paperToProjectId) {
                        <!-- Compact sidebar row: title/meta stacks full-width so it wraps
                             normally in this narrow column; the action sits on its own row
                             below. Do NOT reuse .list-item here — its sm:flex-row layout
                             collapses the title to one-word-per-line in a narrow container. -->
                        <li class="px-3 py-2.5 text-xs border-b border-slate-200 last:border-b-0">
                          <div>
                            <span class="block text-slate-700">{{
                              cp.title || 'Paper ' + cp.paperId
                            }}</span>
                            <span
                              class="block text-xs text-slate-500 mt-0.5"
                              data-testid="completed-meta"
                            >
                              @if (cp.publicationDate) {
                                <span>{{ cp.publicationDate | date: 'yyyy' }}</span>
                              }
                              @if (cp.authors) {
                                <span>
                                  @if (cp.publicationDate) {
                                    <span> &middot; </span>
                                  }
                                  {{ cp.authors }}</span
                                >
                              }
                              @if (cp.doi) {
                                <span>
                                  @if (cp.publicationDate || cp.authors) {
                                    <span> &middot; </span>
                                  }
                                  DOI: {{ cp.doi }}</span
                                >
                              }
                            </span>
                          </div>
                          <div class="mt-2 flex items-center justify-end gap-2">
                            @if (cp.underAdjudication) {
                              <span
                                class="mr-auto text-xs text-slate-500"
                                data-testid="adjudication-note"
                              >
                                PI is resolving a conflict
                              </span>
                            }
                            <button
                              class="btn-small-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                              [disabled]="!!cp.underAdjudication"
                              (click)="onReopenCompleted(cp)"
                              data-testid="view-completed-btn"
                            >
                              View
                            </button>
                          </div>
                        </li>
                      }
                    </ul>
                  }
                </div>
              }
            </div>

            <!-- PI Team section -->
            @if (isPI() && s.researcherProgress && s.researcherProgress.length > 0) {
              <div class="mt-4 pt-4 border-t border-slate-100">
                <div class="flex items-center justify-between">
                  <div>
                    <span class="text-xs font-semibold text-slate-500">
                      <i class="bi bi-people-fill mr-1"></i> Team
                    </span>
                    <span class="text-xs text-slate-400 ml-2"
                      >{{ s.teamTotalExtractions ?? 0 }} /
                      {{ s.teamTotalRequiredExtractions ?? 0 }}</span
                    >
                  </div>
                  <button
                    type="button"
                    (click)="showTeamModal.set(true)"
                    class="text-xs font-semibold text-secondary hover:underline"
                  >
                    View details
                  </button>
                </div>
              </div>
            }
          </div>
        }

        <!-- Agreement Card (PI only) -->
        <div>
          <app-data-extraction-agreement-card
            [visible]="isPI()"
            [projectId]="projectId()"
            [projectState]="projectState()"
            [preferOfficial]="isCompleted()"
            [questions]="questions()"
          ></app-data-extraction-agreement-card>
        </div>
      </div>
    }
  </div>

  <!-- Extraction workspace modal: the two-column editor + save row live here. The Complete
       button and its hints stay on the page (outside the modal). -->
  <app-base-modal
    [open]="workspaceOpen()"
    (openChange)="workspaceOpen.set($event)"
    (closed)="workspaceOpen.set(false)"
    size="full"
    title="Extraction Workspace"
    [closeOnEscape]="!innerOverlayActive()"
    [closeOnBackdrop]="!backdropLocked()"
    data-testid="workspace-modal"
  >
    <div class="relative flex flex-col h-full min-h-0">
      <!-- Two-column row: Questions (left) + PDF Viewer (right) -->
      <div
        #workspaceRow
        class="flex-1 min-h-0 flex flex-col lg:flex-row p-4 select-none"
        [class.cursor-col-resize]="!!dividerDrag()"
      >
        <!-- Questions column -->
        <div
          class="min-w-0 h-full flex flex-col rounded-lg border border-slate-200 overflow-hidden select-auto"
          [style.flex]="'0 0 ' + splitRatio() * 100 + '%'"
        >
          <div class="px-4 py-2 bg-slate-50 border-b border-slate-200 shrink-0">
            <h4 class="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Extraction Questions
            </h4>
          </div>
          <div class="flex-1 overflow-y-auto p-4">
            <app-data-extraction-questionnaire
              [questions]="questions()"
              [answers]="currentAnswers()"
              [annotations]="currentAnnotations()"
              [annotatedQuestionIds]="annotatedQuestionIds()"
              [readOnly]="workspaceReadOnly() || completing() || skipping()"
              [saving]="saving()"
              [resetKey]="currentPaper()?.paperToProjectId ?? null"
              (answerChange)="onAnswerChange($event)"
              (ocrTextClick)="onOcrTextClick($event)"
              (ocrTextSave)="onOcrTextSave($event)"
            ></app-data-extraction-questionnaire>
          </div>
        </div>

        <!-- Draggable divider -->
        <div
          class="shrink-0 w-2 flex items-center justify-center group cursor-col-resize touch-none"
          (pointerdown)="onDividerDragStart($event, workspaceRow)"
        >
          <div
            class="h-10 w-1 rounded-full bg-slate-300 group-hover:bg-blue-500 transition-colors"
            [class.bg-blue-500]="!!dividerDrag()"
          ></div>
        </div>

        <!-- PDF Viewer column -->
        <div
          class="flex-1 min-w-0 h-full flex flex-col rounded-lg border border-slate-200 overflow-hidden select-auto"
        >
          @if (!workspaceReadOnly()) {
            <div
              class="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-sm shrink-0"
            >
              <button
                class="px-2 py-1 border rounded disabled:cursor-not-allowed disabled:opacity-50"
                [class.bg-emerald-500]="drawMode() === 'box'"
                [class.text-white]="drawMode() === 'box'"
                [disabled]="completing() || skipping()"
                (click)="toggleBoxMode()"
                data-testid="draw-box-btn"
              >
                Draw box
              </button>
              <button
                class="px-2 py-1 border rounded disabled:cursor-not-allowed disabled:opacity-50"
                [class.bg-emerald-500]="drawMode() === 'text'"
                [class.text-white]="drawMode() === 'text'"
                [disabled]="completing() || skipping()"
                (click)="toggleTextMode()"
                data-testid="draw-text-btn"
              >
                Draw text
              </button>
            </div>
          }
          <div class="flex-1 overflow-hidden relative bg-slate-100">
            <app-cad-pdf-viewer
              #pdfViewer
              [showZoomBar]="true"
              (pageRendered)="onPageRendered($event)"
              (loadFailed)="onViewerLoadFailed()"
            />
            @for (p of pages(); track p) {
              @if (geometryFor(p); as geom) {
                @if (pageElementFor(p); as pageEl) {
                  <ng-container
                    appClaimAnnotatorOverlay
                    [overlayPageNumber]="p"
                    [overlayDocumentId]="activeDocumentId()"
                    [overlayGeometry]="geom"
                    [overlayPageElement]="pageEl"
                    [overlayDrawMode]="drawMode()"
                    [overlayReadOnly]="workspaceReadOnly()"
                    [overlayHighlight]="highlightedAnnotation()"
                    (overlayDrawCompleted)="onOverlayDraw($event)"
                    (overlayTextPointPicked)="onOverlayTextPoint($event)"
                    (overlayEditNoteRequested)="onEditNoteRequested($event)"
                    (overlayDeleteRequested)="onDeleteAnnotation($event)"
                    (overlayEditOcrRequested)="onEditOcrRequested($event)"
                  ></ng-container>
                }
              }
            }
          </div>
        </div>
      </div>

      <!-- Save row + active-paper error (inside the modal, below the two-column row). -->
      @if (!workspaceReadOnly()) {
        <div class="shrink-0 px-4 pb-4">
          <div class="flex items-center gap-3 pt-4 border-t border-slate-200">
            <button
              [class.btn-primary]="!saveDisabled"
              [class.btn-disabled-primary]="saveDisabled"
              [disabled]="saveDisabled"
              (click)="onSaveProgress()"
              data-testid="save-progress-btn"
            >
              {{ saving() ? 'Saving...' : editingCompleted() ? 'Save changes' : 'Save Progress' }}
            </button>
            @if (hasUnifiedUnsavedChanges() && !saving()) {
              <span class="text-xs text-amber-700">Unsaved changes</span>
            }
            @if (editingCompleted() && !canComplete()) {
              <span class="text-xs text-slate-500" data-testid="save-blocked-reason"
                >Can't save while incomplete: {{ blockedReason() }}</span
              >
            }
          </div>

          @if (lastError()) {
            <div
              class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center gap-2 mt-3"
              data-testid="save-error"
            >
              <span class="flex-1">{{ lastError() }}</span>
              <button
                class="btn-small-secondary border-red-200 text-red-600 hover:bg-red-50"
                (click)="lastError.set(null)"
              >
                Dismiss
              </button>
            </div>
          }
        </div>
      }

      <!-- Inner overlays (claim picker, note editor, text input): rendered inside the modal,
             positioned near the draw/click point and above the modal panel (z-60). -->
      @if (workflow.pendingTextPoint(); as t) {
        <div
          class="fixed z-[60]"
          [style.left.px]="clampX(t.clientX)"
          [style.top.px]="clampY(t.clientY)"
        >
          <app-text-input-overlay
            (confirmed)="onTextInputConfirmed($event)"
            (cancelled)="onTextInputCancelled()"
          />
        </div>
      }
      @if (workflow.pendingDraft()) {
        <div
          class="fixed z-[60]"
          [style.left.px]="clampX(lastDrawClient()?.x ?? 0)"
          [style.top.px]="clampY(lastDrawClient()?.y ?? 0)"
        >
          <app-claim-picker-popover
            [claims]="currentClaims()"
            (picked)="onClaimPicked($event)"
            (cancelled)="onClaimPickerCancelled()"
          />
        </div>
      }
      @if (workflow.editingNoteForId()) {
        <div
          class="fixed z-[60]"
          [style.left.px]="clampX(noteEditorAnchor().x)"
          [style.top.px]="clampY(noteEditorAnchor().y)"
          data-testid="note-editor-anchor"
        >
          <app-note-editor
            [initial]="workflow.noteInitial()"
            (saved)="onNoteSaved($event)"
            (cancelled)="onNoteEditorCancelled()"
          />
        </div>
      }
      @if (workflow.editingOcrForId()) {
        <div
          class="fixed z-[60]"
          [style.left.px]="clampX(noteEditorAnchor().x)"
          [style.top.px]="clampY(noteEditorAnchor().y)"
          data-testid="ocr-editor-anchor"
        >
          <app-ocr-editor
            [initial]="workflow.ocrInitial()"
            (saved)="onOcrSaved($event)"
            (cancelled)="onOcrEditorCancelled()"
          />
        </div>
      }
    </div>
  </app-base-modal>

  <!-- Team Progress Modal -->
  <app-base-modal
    [open]="showTeamModal()"
    (openChange)="showTeamModal.set($event)"
    title="Team Extraction Progress"
    size="md"
  >
    <div class="space-y-2">
      <div
        class="flex justify-between text-xs font-semibold text-slate-500 pb-2 border-b border-slate-100"
      >
        <span>Extractions</span>
        <span
          >{{ stats()?.teamTotalExtractions ?? 0 }} /
          {{ stats()?.teamTotalRequiredExtractions ?? 0 }}</span
        >
      </div>
      <ul class="space-y-1">
        @for (member of stats()?.researcherProgress ?? []; track member.userId) {
          <li
            class="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 border border-slate-100"
          >
            <span class="text-sm text-slate-700 font-medium">
              {{ member.firstName }} {{ member.lastName }}
              @if (member.role === 'PI') {
                <span
                  class="ml-1.5 text-[10px] font-bold text-violet-600 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-full"
                  >PI</span
                >
              }
            </span>
            <span class="text-sm text-slate-500 font-medium"
              >{{ member.completedCount }} / {{ member.totalCount }}</span
            >
          </li>
        }
      </ul>
    </div>
  </app-base-modal>
</div>
